import {Component, computed, effect, inject, input, OnDestroy, output, signal, ViewChild} from '@angular/core';

import {AudioService} from '../../../../services/audio/audio.service';
import {SlideshowDataService} from '../../../../services/slide-show/slideshow-data.service';
import {AlternativePromptsService} from '../../../../services/slide-show/alternative-prompts.service';
import {SlideshowControlService} from '../../../../services/slide-show/slideshow-control.service';

import {VideoControlService} from '../../../../services/slide-show/video-control.service';
import {SlideshowStateService} from '../../../../services/slide-show/slideshow-state.service';
import {KenBurnsService} from '../../../../services/slide-show/ken-burns.service';
import {AudioIntegrationService} from '../../../../services/slide-show/audio-integration.service';
import {VideoPlayerIntegrationService} from '../../../../services/slide-show/video-player-integration.service';
import {SlideProgressService} from '../../../../services/slide-show/slide-progress.service';
import {ProgressBarComponent} from '../components/progress-bar/progress-bar.component';
import {TopControlsComponent} from '../components/top-controls/top-controls.component';
import {SlideDisplayComponent} from '../components/slide-display/slide-display.component';
import {SlideShowModalComponent} from '../components/slide-show-modal/slide-show-modal.component';
import {ContentAreaComponent} from '../components/content-area/content-area.component';
import {KenBurnsEffect} from '../models/ken-burns-effect';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
  duration?: number;
}

@Component({
  selector: 'app-slide-show',
  standalone: true,
  imports: [
    ProgressBarComponent,
    TopControlsComponent,
    SlideDisplayComponent,
    SlideShowModalComponent,
    ContentAreaComponent],
  templateUrl: './slide-show.component.html',
  styleUrl: './slide-show.component.scss'
})
export class SlideShowComponent implements OnDestroy {
  // Services (inject first)
  public audioService = inject(AudioService);
  // Component references
  @ViewChild('slideDisplay') slideDisplay?: SlideDisplayComponent;
  // Inputs
  mediaItems = input<MediaItem[]>([]);
  isVisible = input(false);
  // Outputs
  isVisibleChange = output<boolean>();
  // Internal state
  currentIndex = signal(0);
  isPlaying = signal(false);
  showControls = signal(true);
  kenBurnsEffects = signal<KenBurnsEffect[]>([]);

  // Computed properties
  readonly currentMediaItem = computed(() => {
    const items = this.mediaItems();
    const index = this.currentIndex();
    return items[index] || null;
  });
  readonly hasMedia = computed(() => this.mediaItems().length > 0);
  readonly isLastSlide = computed(() => this.currentIndex() >= this.mediaItems().length - 1);
  readonly isFirstSlide = computed(() => this.currentIndex() === 0);
  // Current song info
  readonly currentSong = computed(() => {
    const tracks = this.audioService.tracks();
    const currentIndex = this.audioService.currentTrackIndex();
    return tracks[currentIndex] || null;
  });
  isSlideContentHovered = signal(false);
  mobileControlsVisible = signal(false); // Mobile-specific control visibility
  readonly hasAudioResults = computed(() => this.audioResults().length > 0);
  // Current description with fallback
  readonly currentDescription = computed(() => {
    const brief = this.briefDescription();
    if (brief) return brief;

    return 'These songs represent the soundtrack of your life - ' +
      'each one chosen for the memories they hold, the emotions they evoke, ' +
      'and the moments they\'ve accompanied you through.';
  });
  private slideshowDataService = inject(SlideshowDataService);
  // Audio and slideshow data computed properties
  readonly audioResults = computed(() => this.slideshowDataService.audioResults());
  readonly briefDescription = computed(() => this.slideshowDataService.briefDescription());
  private alternativePromptsService = inject(AlternativePromptsService);
  readonly isLoadingAlternatives = computed(() => this.alternativePromptsService.isLoadingAlternatives());
  private slideshowControlService = inject(SlideshowControlService);
  private videoControlService = inject(VideoControlService);
  private slideshowStateService = inject(SlideshowStateService);
  // Delegate state properties to SlideshowStateService

  readonly isTransitioning = computed(() => this.slideshowStateService.isTransitioning());
  private kenBurnsService = inject(KenBurnsService);
  private audioIntegrationService = inject(AudioIntegrationService);
  private videoPlayerIntegrationService = inject(VideoPlayerIntegrationService);
  private slideProgressService = inject(SlideProgressService);

  // Progress tracking (delegated to service)
  readonly progressPercentage = this.slideProgressService.progressPercentage;

  // Add missing state flags for proper audio looping and song switching
  private isSwitchingSongs = false;
  private songEndedNaturally = false;
  // Track previous audio playing state to detect transitions
  private previousAudioPlaying = false;
  // Timing patterns for natural variation
  private timingVariations = [0.9, 1.0, 1.1, 0.95, 1.05, 1.0, 0.85, 1.15];
  // Animation and timing management
  private slideInterval?: number;
  private controlsTimeout?: number;

  // Mobile touch handling
  private touchStartTime: number = 0;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private mobileControlsHideTimer: any = null;
  private readonly MOBILE_CONTROLS_HIDE_DELAY = 5000; // 5 seconds
  private readonly SWIPE_THRESHOLD = 50; // pixels
  private readonly TAP_TIME_THRESHOLD = 300; // milliseconds

  // Flag to track touch events

  constructor() {
    // Initialize mobile controls visibility
    if (this.isMobile()) {
      this.mobileControlsVisible.set(false);
    }

    // Initialize Ken Burns effects when media items change
    effect(() => {
      const items = this.mediaItems();
      if (items.length > 0) {
        const effects = this.kenBurnsService.generateEffectsForMediaItems(
          items.map(item => ({
            type: item.type,
            duration: item.duration || 0
          })),
          this.calculateSlideDurations(items)
        );
        this.kenBurnsEffects.set(effects);
      }
    });


    // Auto-hide controls after user interaction
    effect(() => {
      if (this.showControls()) {
        this.resetControlsTimeout();
      }
    });

    // Reset video state when switching between media items
    effect(() => {
      const currentMedia = this.currentMediaItem();
      if (currentMedia?.type === 'video') {
        this.videoPlayerIntegrationService.resetVideoState();
      }
    });

    effect(() => {
      if (this.isVisible()) {
        document.body.classList.add('no-scroll');
      } else {
        document.body.classList.remove('no-scroll');
      }
    });

    // CONSOLIDATED audio initialization and auto-play effect
    let wasVisible = false;
    let previousAudioResults: any[] = [];
    let autoPlayAttempts = 0; // Track auto-play attempts for older hardware

    effect(() => {
      const isVisible = this.isVisible();
      const audioResults = this.audioResults();

      // Track visibility changes
      const visibilityChanged = isVisible !== wasVisible;
      wasVisible = isVisible;

      if (isVisible && audioResults.length > 0) {
        // More reliable way to detect new results - compare actual track URLs/content
        const hasNewResults = audioResults.length > 0 && (
          previousAudioResults.length === 0 || // First time
          audioResults.length !== previousAudioResults.length || // Different count
          !audioResults.every((track, index) =>
            previousAudioResults[index] &&
            track.url === previousAudioResults[index].url
          ) // Different tracks
        );

        const needsInitialization = visibilityChanged || hasNewResults;

        if (needsInitialization) {
          console.log('Enhanced slideshow: Initializing audio tracks', {
            visibilityChanged,
            hasNewResults,
            trackCount: audioResults.length,
            mediaItemsCount: this.mediaItems().length,
            autoPlayAttempts: autoPlayAttempts
          });

          // For alternative prompts (when hasNewResults but not visibility change),
          // add a larger delay to ensure media items are available, especially on older hardware
          const conversionDelay = hasNewResults && !visibilityChanged ? 250 : 0; // Increased from 100ms

          setTimeout(() => {
            console.log('Converting audio tracks after delay, media items now:', this.mediaItems().length);

            try {
              // Convert raw API results to properly formatted AudioTrack objects
              const audioTracks = this.audioIntegrationService.convertToAudioTracks(
                audioResults,
                this.mediaItems(),
                this.currentDescription()
              );
              console.log('Enhanced slideshow: Converted tracks:', audioTracks.length, 'tracks');
              console.log('Enhanced slideshow: Setting tracks on audio service...');

              // Initialize audio tracks in the audio service with formatted data
              this.audioService.setTracks(audioTracks);

              console.log('Enhanced slideshow: Audio service tracks set. Current state:', {
                tracksCount: this.audioService.tracks().length,
                isPlaying: this.audioService.isPlaying(),
                isLoading: this.audioService.isLoading(),
                currentIndex: this.audioService.currentTrackIndex()
              });
            } catch (error) {
              console.error('Enhanced slideshow: Error converting audio tracks:', error);
              // Continue with auto-play attempt even if conversion partially failed
            }
          }, conversionDelay);

          // Start state service monitoring when slideshow becomes visible
          if (visibilityChanged) {
            console.log('Enhanced slideshow: Starting state service monitoring');
            this.slideshowStateService.startAudioMonitoring();
            this.slideshowStateService.startHeartbeatCheck();

            // Calculate audio loop requirement based on slideshow vs audio duration
            this.slideshowStateService.calculateAudioLoopRequirement(this.mediaItems());

            // Reset auto-play attempts counter on new visibility
            autoPlayAttempts = 0;
          }

          // Auto-play the first song when slideshow first becomes visible
          // OR when new alternative results are loaded
          const shouldAutoPlay = !this.audioService.isPlaying() &&
            !this.isSwitchingSongs &&
            !this.songEndedNaturally &&
            (visibilityChanged || hasNewResults);

          if (shouldAutoPlay) {
            console.log(visibilityChanged ? 'Enhanced slideshow became visible - starting initial playback'
              : 'New alternative results loaded - starting playback');

            // Increased delay for older hardware compatibility and add retry mechanism
            const baseDelay = visibilityChanged ? 500 : 400; // Increased from 300ms
            const retryDelay = autoPlayAttempts * 200; // Progressive delay for retries
            const totalDelay = baseDelay + retryDelay;

            setTimeout(() => {
              this.attemptAutoPlay(0);
            }, totalDelay);
          }
        }

        // Update previous results for next comparison
        previousAudioResults = [...audioResults];
      }
    });

    // Visual slideshow management - synchronized with audio play/pause
    effect(() => {
      const isVisible = this.isVisible();
      const hasMedia = this.hasMedia();
      const audioIsPlaying = this.audioService.isPlaying(); // Only playing, not loading
      const isTransitioning = this.isTransitioning();

      // Don't make slideshow changes during transitions (song changes, prompt refreshes)
      if (isTransitioning) {
        console.log('Skipping slideshow management during transition');
        return;
      }

      // Start slideshow only when audio is actually playing (not just loading)
      if (isVisible && hasMedia && audioIsPlaying) {
        // Only start if not already playing visual slideshow
        if (!this.isPlaying()) {
          console.log('Starting visual slideshow (audio playing)');
          this.play();
        }
      } else if (isVisible && hasMedia && !audioIsPlaying) {
        // Pause slideshow when audio pauses or is loading (but slideshow is still visible)
        if (this.isPlaying()) {
          console.log('Pausing visual slideshow (audio not playing)');
          this.pause();
        }
      } else if (!isVisible) {
        // Pause slideshow when slideshow is hidden
        this.pause();
      }
    });

    // Connect isPlaying signal to actual slideshow mechanics using services
    effect(() => {
      if (this.isPlaying() && this.hasMedia()) {
        console.log('Visual slideshow isPlaying = true, starting slideshow via service');
        this.startSlideshowWithServices();
      } else {
        console.log('Visual slideshow isPlaying = false, pausing slideshow via service');
        this.slideshowControlService.pauseSlideshow();
      }
    });

    // NEW: Effect to handle song ending - loop current song if slideshow is still active
    effect(() => {
      const isPlaying = this.audioService.isPlaying();
      const currentTime = this.audioService.currentTime();
      const endTime = this.audioService.endTime();
      const duration = this.audioService.duration();
      const isVisible = this.isVisible();
      const userPaused = this.slideshowStateService.userPaused();

      // Track state transitions - detect when audio stops playing
      const audioJustStopped = this.previousAudioPlaying && !isPlaying;
      this.previousAudioPlaying = isPlaying;

      // Debug logging when audio stops
      if (audioJustStopped && endTime && endTime > 0 && currentTime > 0) {
        console.log(`Enhanced slideshow: Audio just stopped - currentTime: ${currentTime}, endTime: ${endTime}, userPaused: ${userPaused}, isSwitchingSongs: ${this.isSwitchingSongs}, songEndedNaturally: ${this.songEndedNaturally}`);
      }

      // Detect when song ends naturally (either at endTime or full duration)
      const songEnded = audioJustStopped && (
        (endTime && endTime > 0 && currentTime >= endTime - 2.0) || // Song with endTime (more tolerance)
        ((!endTime || endTime === 0) && duration > 0 && currentTime >= duration - 2.0) // Full song (more tolerance)
      );

      // Additional check: if we just stopped and we're close to the end time
      const nearEndAndStopped = audioJustStopped && endTime && endTime > 0 && currentTime > (endTime - 5.0);

      // For the enhanced slideshow, ignore userPaused state when song ends naturally
      // because the audio service may not update userPaused correctly when song ends at endTime
      const shouldLoop = (songEnded || nearEndAndStopped) &&
        isVisible &&
        !this.isSwitchingSongs &&
        !this.songEndedNaturally; // Prevent multiple triggers

      if (shouldLoop) {
        // Set flag to prevent other effects from auto-playing song 0
        this.songEndedNaturally = true;

        // When a song ends naturally, loop the current song to continue the slideshow
        // Only advance to next song when user manually navigates
        const currentIndex = this.audioService.currentTrackIndex();
        console.log(`Song ended naturally (currentTime: ${currentTime}, endTime: ${endTime}), looping current song ${currentIndex} to continue slideshow`);

        setTimeout(() => {
          console.log('Seamlessly looping current song to continue slideshow');
          const loopSuccess = this.audioService.loopCurrentTrackSeamlessly();
          if (!loopSuccess) {
            console.log('Seamless loop failed, falling back to regular loop');
            this.audioService.loopCurrentTrack();
          } else {
            // For automatic loops, let slideshow continue from current position
            console.log('Seamless loop successful - slideshow continues from current position');
          }
          // Reset the flag after looping starts
          setTimeout(() => {
            this.songEndedNaturally = false;
          }, 1000);
        }, 500);
      }
    });
  }

  ngOnDestroy(): void {
    // Stop video progress animation
    this.videoPlayerIntegrationService.stopSmoothVideoProgress();

    // Stop audio immediately and perform full cleanup
    this.audioService.stop(true); // immediate stop without fadeout
    this.audioService.cleanup(); // full resource cleanup

    // State service cleanup
    this.slideshowStateService.stopAudioMonitoring();
    this.slideshowStateService.stopHeartbeatCheck();

    this.cleanup();

    // Service cleanup
    this.slideshowControlService.cleanup();
    this.videoControlService.reset();
    this.alternativePromptsService.reset();

    // Reset state flags
    this.isSwitchingSongs = false;
    this.songEndedNaturally = false;
    this.previousAudioPlaying = false;

    // Remove no-scroll class
    document.body.classList.remove('no-scroll');
  }

  // Public methods
  play(): void {
    // Resume from where we left off using the service
    this.slideProgressService.play(this.slideProgressService.getPausedElapsed());
    this.isPlaying.set(true);
  }

  pause(): void {
    // Store elapsed time when pausing using the service
    this.slideProgressService.pause();
    this.isPlaying.set(false);
  }

  togglePlayPause(): void {
    // Clear any tab switch flags since this is a manual user interaction
    this.slideshowStateService.clearTabSwitchFlag();

    // Update user pause state based on current playing state
    // If currently playing, user is about to pause it
    // If currently paused, user is about to resume playback
    this.slideshowStateService.setUserPaused(this.audioService.isPlaying());

    console.log(`User ${this.audioService.isPlaying() ? 'pausing' : 'resuming'} audio playback`);

    // Special handling for restart after song ends
    if (!this.audioService.isPlaying() && this.songEndedNaturally) {
      console.log('Song had ended naturally - seamlessly restarting current song');
      const loopSuccess = this.audioService.loopCurrentTrackSeamlessly();
      if (loopSuccess) {
        // Reset the flag since we're manually restarting
        this.songEndedNaturally = false;
        // Restart slideshow from first slide for manual restart
        console.log('Manual seamless restart - restarting slideshow from first slide');
        this.currentIndex.set(0);
        this.slideProgressService.resetPausedElapsed(); // Reset elapsed time
        this.resetSlideProgress();
        return;
      } else {
        console.log('Seamless restart failed, falling back to full restart');
        const currentIndex = this.audioService.currentTrackIndex();
        this.playSong(currentIndex);
        return;
      }
    }

    // Toggle the audio playback via the audio service
    this.audioService.togglePlayPause();
  }

  nextSlide(): void {
    this.slideProgressService.resetPausedElapsed(); // Reset paused time when manually navigating

    // Stop current slideshow timing before advancing
    this.slideshowControlService.pauseSlideshow();
    this.pauseSlideshow();

    this.slideshowControlService.advanceToNextMedia(
      this.mediaItems(),
      this.currentIndex(),
      (newIndex) => {
        this.currentIndex.set(newIndex);
        // Restart slideshow timing for new slide if playing
        if (this.isPlaying()) {
          console.log('Manual next - restarting slideshow timing');
          this.resetSlideProgress();
        }
      },
      () => this.startSlideshowWithServices()
    );
  }

  previousSlide(): void {
    this.slideProgressService.resetPausedElapsed(); // Reset paused time when manually navigating

    // Stop current slideshow timing before going back
    this.slideshowControlService.pauseSlideshow();
    this.pauseSlideshow();

    this.slideshowControlService.goToPreviousMedia(
      this.mediaItems(),
      this.currentIndex(),
      (newIndex) => {
        this.currentIndex.set(newIndex);
        // Restart slideshow timing for new slide if playing
        if (this.isPlaying()) {
          console.log('Manual previous - restarting slideshow timing');
          this.resetSlideProgress();
        }
      },
      () => this.startSlideshowWithServices()
    );
  }

  close(): void {
    console.log('Enhanced slideshow: Closing slideshow - stopping audio and cleaning up');

    // Stop visual slideshow
    this.pause();

    // Stop and cleanup audio completely (like the old slideshow component)
    this.audioService.stop(true); // immediate stop without fadeout
    this.audioService.cleanup(); // full resource cleanup

    // Stop state service monitoring
    this.slideshowStateService.stopAudioMonitoring();
    this.slideshowStateService.stopHeartbeatCheck();
    this.slideshowStateService.resetSlideshow();

    // Reset component state
    this.isSwitchingSongs = false;
    this.songEndedNaturally = false;
    this.previousAudioPlaying = false;

    // Emit visibility change and cleanup visual resources
    this.isVisibleChange.emit(false);
    this.cleanup();

    console.log('Enhanced slideshow: Close complete - audio stopped and resources cleaned up');
  }

  // Audio control methods
  playSong(index: number): void {
    const audioResults = this.audioResults();
    if (audioResults.length > 0 && index >= 0 && index < audioResults.length) {
      console.log(`Playing song at index ${index} from slideshow data`);

      // Log diagnostic info before transition
      const diagBefore = this.audioService.getDiagnosticInfo();
      console.log('Audio state before song change:', diagBefore);

      // Set flag to prevent auto-play effect from interfering during song switch
      this.isSwitchingSongs = true;

      // Reset the natural ending flag since we're starting new playback
      this.songEndedNaturally = false;

      // Set transitioning state to prevent unwanted slideshow behavior during song change
      this.slideshowStateService.setTransitioning(true);

      // Stop current slideshow before switching songs
      this.pause();
      this.slideshowControlService.pauseSlideshow();

      // Reset paused elapsed time so new song starts slideshow fresh from 0
      this.slideProgressService.resetPausedElapsed();

      // Reset to first slide when changing songs
      this.currentIndex.set(0);
      console.log('Song change - resetting slideshow timing and returning to first slide');

      // Reset user paused state since user is actively playing a song
      this.slideshowStateService.setUserPaused(false);

      // For older hardware, add a small delay to ensure cleanup completes before new track
      const isOlderDevice = this.detectOlderDevice();
      if (isOlderDevice) {
        console.log('Older device detected - adding delay before track change');
        setTimeout(() => {
          this.audioService.playTrack(index);
          console.log('Audio state after song change (delayed):', this.audioService.getDiagnosticInfo());
        }, 100);
      } else {
        // Play the new song immediately on modern devices
        this.audioService.playTrack(index);
        console.log('Audio state after song change:', this.audioService.getDiagnosticInfo());
      }

      // Reset transitioning state after a short delay
      setTimeout(() => {
        this.slideshowStateService.setTransitioning(false);
        // Clear the flag after a short delay to allow the audio to start playing
        this.isSwitchingSongs = false;
        // The effect system will restart the slideshow when new song starts playing

        // Final diagnostic check
        setTimeout(() => {
          const diagAfter = this.audioService.getDiagnosticInfo();
          console.log('Final audio state after transition:', diagAfter);
          if (!diagAfter.isPlaying && !diagAfter.isLoading) {
            console.warn('⚠️ Song transition may have failed - audio not playing after transition');
          }
        }, 1000);
      }, 500);
    } else {
      console.warn(`Invalid audio index: ${index} (available: ${audioResults.length})`);
    }
  }

  playPreviousSong(): void {
    const currentIndex = this.audioService.currentTrackIndex();
    if (currentIndex > 0) {
      this.playSong(currentIndex - 1);
    }
  }

  playNextSong(): void {
    const currentIndex = this.audioService.currentTrackIndex();
    const audioResults = this.audioResults();
    if (currentIndex < audioResults.length - 1) {
      this.playSong(currentIndex + 1);
    }
  }

  onVolumeUpdated(volume: number): void {
    this.audioService.updateVolume(volume);
  }

  onVideoTimeUpdate(): void {
    this.videoPlayerIntegrationService.onVideoTimeUpdate();

    // Force sync video state periodically to fix display issues
    if (this.slideDisplay && Math.random() < 0.01) { // 1% chance per timeupdate
      this.slideDisplay.forceSyncVideoState();
    }
  }

  onVideoLoadedMetadata(): void {
    this.videoPlayerIntegrationService.onVideoLoadedMetadata();

    // Force sync video state when metadata loads to ensure proper initialization
    setTimeout(() => {
      if (this.slideDisplay) {
        this.slideDisplay.forceSyncVideoState();
      }
    }, 50);
  }

  onVideoPlay(): void {
    this.videoPlayerIntegrationService.onVideoPlay();

    // Force sync video state when video starts playing to ensure proper display
    setTimeout(() => {
      if (this.slideDisplay) {
        this.slideDisplay.forceSyncVideoState();
      }
    }, 100);
  }

  onVideoPause(): void {
    this.videoPlayerIntegrationService.onVideoPause();
  }

  onVideoEnded(): void {
    this.videoPlayerIntegrationService.onVideoEnded(() => {
      const mediaItems = this.mediaItems();
      const currentIndex = this.currentIndex();
      const videoItems = mediaItems.filter(item => item.type === 'video');

      // Case 1: Only one video - loop it back to the beginning
      if (videoItems.length === 1) {
        console.log('Single video ended - looping back to beginning');
        this.restartCurrentVideo();
        return;
      }

      // Case 2: Multiple videos - check if we're at the last video
      if (videoItems.length > 1) {
        const isLastVideo = this.isLastVideoInSequence(mediaItems, currentIndex);
        if (isLastVideo) {
          console.log('Last video in sequence ended - looping back to first video');
          this.loopToFirstVideo();
          return;
        }
      }

      // Case 3: Mixed media or not at end - continue with normal progression
      const audioIsPlaying = this.audioService.isPlaying();
      if (audioIsPlaying) {
        this.nextSlide(); // Can't restart video without event
      } else {
        this.nextSlide();
      }
    });
  }

  // Alternative prompts functionality
  handleRequestAlternativePrompt(): void {
    console.log('Requesting alternative prompts - pausing slideshow');

    // Set transitioning state during prompt refresh
    this.slideshowStateService.setTransitioning(true);

    // Audio is paused by the alt-prompts service; stopping here would redundantly unload the Howl.
    this.pause();
    this.slideshowControlService.pauseSlideshow();

    // Reset state flags so new results will auto-play
    this.isSwitchingSongs = false;
    this.songEndedNaturally = false;
    this.previousAudioPlaying = false;

    // Request new prompts
    this.alternativePromptsService.handleRequestAlternativePrompt();

    // Reset transitioning state after a short delay
    setTimeout(() => {
      this.slideshowStateService.setTransitioning(false);
      // The audio initialization effect will restart everything when new results arrive
    }, 500);
  }

  onSlideClick(): void {
    this.showControls.set(true);
  }

  onSegmentClick(segmentIndex: number): void {
    // Set the current index to the clicked segment
    this.currentIndex.set(segmentIndex);
    // Reset and restart the slideshow progress for the new slide
    this.resetSlideProgress();
  }

  getCurrentKenBurnsEffect(): KenBurnsEffect | null {
    const effects = this.kenBurnsEffects();
    const index = this.currentIndex();
    return effects[index] || null;
  }

  onSlideContentMouseEnter(): void {
    this.isSlideContentHovered.set(true);
  }

  onSlideContentMouseLeave(): void {
    this.isSlideContentHovered.set(false);
  }

  // Mobile touch event handlers for template
  onVideoTouchStart(event: TouchEvent): void {
    console.log('🎞️ SlideShow: Video touch start received from child');
    this.handleVideoTouchStart(event);
  }

  onVideoTouchEnd(event: TouchEvent): void {
    console.log('🎞️ SlideShow: Video touch end received from child');
    this.handleVideoTouchEnd(event);
  }

  // When user touches video controls, keep them visible and restart timer
  onVideoControlsTouch(): void {
    console.log('🎞️ SlideShow: Video controls touch received', {isMobile: this.isMobile()});
    if (!this.isMobile()) return;

    this.mobileControlsVisible.set(true);
    this.startMobileControlsHideTimer();
  }

  // Computed property for control visibility that works on both mobile and desktop
  getControlsVisible(): boolean {
    const isMobile = this.isMobile();
    const mobileVisible = this.mobileControlsVisible();
    const hovered = this.isSlideContentHovered();

    return isMobile ? mobileVisible : hovered;
  }

  // Private methods
  private calculateSlideDurations(items: MediaItem[]): number[] {
    return items.map((item, index) => {
      let baseDuration = item.type === 'image' ? 5000 : Math.min(item.duration || 8000, 15000);

      // Apply natural variation
      const variation = this.timingVariations[index % this.timingVariations.length];
      baseDuration *= variation;

      // First and last slides get more time
      if (index === 0 || index === items.length - 1) {
        baseDuration *= 1.2;
      }

      // Clamp between 3-12 seconds
      return Math.max(3000, Math.min(12000, baseDuration));
    });
  }

  private startSlideshowWithServices(): void {
    console.log(`Starting slideshow with services for slide ${this.currentIndex() + 1}/${this.mediaItems().length}`);

    // Use the professional slideshow control service
    this.slideshowControlService.startSlideshow(
      this.mediaItems(),
      this.currentIndex(),
      () => this.advanceToNextMedia()
    );

    // Still track progress for the progress bar using the service
    this.slideProgressService.startProgressTracking(
      this.currentIndex(),
      this.mediaItems(),
      () => this.isPlaying()
    );
  }

  private advanceToNextMedia(): void {
    if (this.isLastSlide()) {
      // Always loop back to the beginning - slideshow should always be looping
      console.log('Reached last slide - looping back to first slide');
      this.currentIndex.set(0);
      this.slideProgressService.resetPausedElapsed(); // Reset paused time for new slide
      this.startSlideshowWithServices();
    } else {
      console.log('Auto-advancing to next slide');
      this.nextSlide();
    }
  }

  private pauseSlideshow(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
      this.slideInterval = undefined;
    }

    this.slideProgressService.pauseProgressTracking();
  }

  private resetSlideProgress(): void {
    this.slideProgressService.resetSlideProgress(
      this.currentIndex(),
      this.mediaItems(),
      () => this.isPlaying(),
      () => this.startSlideshowWithServices()
    );
  }

  private resetControlsTimeout(): void {
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }

    this.controlsTimeout = window.setTimeout(() => {
      if (this.isPlaying()) {
        this.showControls.set(false);
      }
    }, 3000); // Hide controls after 3 seconds
  }

  // Mobile touch handling methods
  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  private clearMobileControlsHideTimer(): void {
    if (this.mobileControlsHideTimer) {
      clearTimeout(this.mobileControlsHideTimer);
      this.mobileControlsHideTimer = null;
    }
  }

  private startMobileControlsHideTimer(): void {
    if (!this.isMobile()) return;

    this.clearMobileControlsHideTimer();
    this.mobileControlsHideTimer = setTimeout(() => {
      this.mobileControlsVisible.set(false);
    }, this.MOBILE_CONTROLS_HIDE_DELAY);
  }

  private handleVideoTouchStart(event: TouchEvent): void {
    if (!this.isMobile()) {
      return;
    }

    const touch = event.touches[0];
    this.touchStartTime = Date.now();
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  private handleVideoTouchEnd(event: TouchEvent): void {
    if (!this.isMobile()) {
      return;
    }

    const touch = event.changedTouches[0];
    const touchEndTime = Date.now();
    const touchDuration = touchEndTime - this.touchStartTime;
    const touchDistanceX = Math.abs(touch.clientX - this.touchStartX);
    const touchDistanceY = Math.abs(touch.clientY - this.touchStartY);
    const touchDistance = Math.sqrt(touchDistanceX * touchDistanceX + touchDistanceY * touchDistanceY);

    // Check if this is a tap (not a swipe)
    const isTap = touchDuration < this.TAP_TIME_THRESHOLD && touchDistance < this.SWIPE_THRESHOLD;

    if (isTap) {
      this.handleVideoTap();
    }
  }

  private handleVideoTap(): void {
    const currentlyVisible = this.mobileControlsVisible();

    if (currentlyVisible) {
      // If controls are visible, hide them
      this.mobileControlsVisible.set(false);
      this.clearMobileControlsHideTimer();
    } else {
      // If controls are hidden, show them and start the timer
      this.mobileControlsVisible.set(true);
      this.startMobileControlsHideTimer();
    }
  }

  private cleanup(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
      this.slideInterval = undefined;
    }

    this.slideProgressService.cleanup();

    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
      this.controlsTimeout = undefined;
    }

    // Clean up mobile controls timer
    this.clearMobileControlsHideTimer();
  }

  private attemptAutoPlay(index: number): void {
    const maxRetries = 3;
    let retryCount = 0;

    const tryPlay = () => {
      console.log(`Attempting auto-play (attempt ${retryCount + 1}/${maxRetries})`);

      // Use the audio service's readiness check
      const isReady = this.audioService.isReadyForPlayback();
      const tracks = this.audioService.tracks();
      const isLoading = this.audioService.isLoading();
      const isPlaying = this.audioService.isPlaying();

      console.log('Audio service state:', {
        isReady,
        tracksCount: tracks.length,
        isLoading,
        isPlaying,
        targetIndex: index
      });

      // Check if audio service is ready for playback
      if (!isReady) {
        console.log('Audio service not ready yet, retrying...');
        retryCount++;
        if (retryCount < maxRetries) {
          // Progressive delay: 200ms, 400ms, 600ms
          setTimeout(tryPlay, 200 * retryCount);
        } else {
          console.warn('Auto-play failed: Audio service not ready after maximum retries');
        }
        return;
      }

      // Ensure index is valid
      if (index >= tracks.length) {
        console.warn(`Auto-play failed: Invalid index ${index}, only ${tracks.length} tracks available`);
        return;
      }

      // Don't auto-play if already playing
      if (isPlaying) {
        console.log('Audio already playing, skipping auto-play');
        return;
      }

      // For older hardware, add extra delay to ensure Howl cleanup is complete
      const isOlderDevice = this.detectOlderDevice();
      const extraDelay = isOlderDevice ? 150 : 0;

      if (extraDelay > 0) {
        console.log(`Detected potentially older device, adding ${extraDelay}ms extra delay`);
        setTimeout(() => this.executeAutoPlay(index), extraDelay);
      } else {
        this.executeAutoPlay(index);
      }
    };

    tryPlay();
  }

  private executeAutoPlay(index: number): void {
    try {
      console.log(`Executing auto-play for track ${index}`);
      this.playSong(index);

      // Verify playback started within reasonable time
      setTimeout(() => {
        const isPlaying = this.audioService.isPlaying();
        const isLoading = this.audioService.isLoading();

        if (!isPlaying && !isLoading) {
          console.warn('Auto-play may have failed - audio not playing or loading after attempt');
          this.logDetailedDiagnostics('Auto-play verification failed');
        } else {
          console.log('Auto-play verification: audio is playing or loading successfully');
        }
      }, 500);

    } catch (error) {
      console.error('Error during auto-play execution:', error);
      this.logDetailedDiagnostics('Auto-play execution error', error);
    }
  }

  private logDetailedDiagnostics(reason: string, error?: any): void {
    const diagnostics = {
      reason,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      audioService: {
        isReadyForPlayback: this.audioService.isReadyForPlayback(),
        tracksCount: this.audioService.tracks().length,
        isPlaying: this.audioService.isPlaying(),
        isLoading: this.audioService.isLoading(),
        currentTrackIndex: this.audioService.currentTrackIndex(),
        volume: this.audioService.volume(),
        duration: this.audioService.duration(),
        currentTime: this.audioService.currentTime()
      },
      slideshow: {
        isVisible: this.isVisible(),
        hasMedia: this.hasMedia(),
        mediaItemsCount: this.mediaItems().length,
        currentIndex: this.currentIndex(),
        isPlaying: this.isPlaying(),
        audioResultsCount: this.audioResults().length
      },
      flags: {
        isSwitchingSongs: this.isSwitchingSongs,
        songEndedNaturally: this.songEndedNaturally,
        isTransitioning: this.isTransitioning()
      }
    };

    console.group('🚨 Slideshow Auto-Play Diagnostics');
    console.warn('Reason:', reason);
    if (error) {
      console.error('Error:', error);
    }
    console.table(diagnostics.audioService);
    console.table(diagnostics.slideshow);
    console.table(diagnostics.flags);
    console.log('Full diagnostics object:', diagnostics);
    console.groupEnd();

    // Also log a condensed version for easy reporting
    const condensed = `AutoPlay Issue: ${reason} | Tracks: ${diagnostics.audioService.tracksCount} | Ready: ${diagnostics.audioService.isReadyForPlayback} | Playing: ${diagnostics.audioService.isPlaying} | Loading: ${diagnostics.audioService.isLoading} | UA: ${navigator.userAgent.substring(0, 100)}...`;
    console.warn(condensed);
  }

  private detectOlderDevice(): boolean {
    // Simple heuristics to detect potentially older/slower devices
    const userAgent = navigator.userAgent.toLowerCase();
    const now = Date.now();

    // Test basic performance
    const start = performance.now();
    // Simple computation to test device speed
    let sum = 0;
    for (let i = 0; i < 10000; i++) {
      sum += Math.random();
    }
    const computationTime = performance.now() - start;

    // Consider device older/slower if:
    // - Computation takes longer than expected (> 5ms for simple loop)
    // - User agent indicates older Safari/WebKit versions
    // - macOS version indicators from 2017 era
    const isSlowComputation = computationTime > 5;
    const hasOlderWebKit = /webkit\/[45]\d\d/.test(userAgent); // WebKit 4xx-5xx versions
    const hasOlderSafari = /safari\/[56]\d\d/.test(userAgent); // Safari 5xx-6xx versions  

    const isOlderDevice = isSlowComputation || hasOlderWebKit || hasOlderSafari;

    if (isOlderDevice) {
      console.log('Device characteristics suggest older/slower hardware:', {
        computationTime: computationTime.toFixed(2) + 'ms',
        hasOlderWebKit,
        hasOlderSafari
      });
    }

    return isOlderDevice;
  }

  /**
   * Restart the current video from the beginning (for single video looping)
   */
  private restartCurrentVideo(): void {
    const currentIndex = this.currentIndex();

    // Restart the video using the slide display component
    if (this.slideDisplay) {
      this.slideDisplay.restartVideo();
    }

    // Reset slide progress
    this.slideProgressService.resetSlideProgress(
      currentIndex,
      this.mediaItems(),
      () => this.isPlaying(),
      () => this.startSlideshowWithServices()
    );

    // Restart the slideshow with the same video
    this.startSlideshowWithServices();
  }

  /**
   * Check if the current video is the last video in the sequence
   */
  private isLastVideoInSequence(mediaItems: MediaItem[], currentIndex: number): boolean {
    const currentItem = mediaItems[currentIndex];

    // Must be a video to be considered
    if (!currentItem || currentItem.type !== 'video') {
      return false;
    }

    // Find all video indices after the current index
    const videosAfterCurrent = mediaItems.slice(currentIndex + 1)
      .some(item => item.type === 'video');

    // If no videos after current, this is the last video
    return !videosAfterCurrent;
  }

  /**
   * Loop back to the first video in the sequence
   */
  private loopToFirstVideo(): void {
    const mediaItems = this.mediaItems();
    const firstVideoIndex = mediaItems.findIndex(item => item.type === 'video');

    if (firstVideoIndex >= 0) {
      console.log(`Looping back to first video at index ${firstVideoIndex}`);

      // Jump to first video
      this.currentIndex.set(firstVideoIndex);

      // Reset slide progress
      this.slideProgressService.resetSlideProgress(
        firstVideoIndex,
        mediaItems,
        () => this.isPlaying(),
        () => this.startSlideshowWithServices()
      );

      // Restart the slideshow
      this.startSlideshowWithServices();
    }
  }
}
